import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { capture } from "../lib/analytics";
import { PrimaryButton, ErrorText, Muted, GildedFrame, Spinner } from "./ui";

// The consent gate is enforced SERVER-SIDE (consent.ts, CONSENT_ENFORCED=true) on
// every path that generates from a likeness. The server refuses without it, so
// this component exists to let a user actually give it — nothing here is the
// security boundary, and nothing here is trusted. Blocking, before first upload.

export function ConsentGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<"loading" | "needed" | "ok">("loading");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const c = await api.consent();
        setState(c.accepted ? "ok" : "needed");
      } catch {
        // If we can't read consent, ask again rather than assume it's given —
        // the server would refuse anyway, and a confusing 403 mid-upload is worse.
        setState("needed");
      }
    })();
  }, []);

  if (state === "loading") return <Spinner />;
  if (state === "ok") return <>{children}</>;

  return (
    <GildedFrame className="mx-auto max-w-lg">
      <div className="p-5">
        <h2 className="font-serif text-2xl text-ivory">Before you add a photo</h2>
        <Muted className="mt-1">One quick confirmation. This is required to generate.</Muted>

        <ul className="mt-4 space-y-2.5 text-[14px] leading-relaxed text-parchment">
          <li className="flex gap-2.5">
            <span className="text-gold">✦</span>
            <span>The photo is of me, an adult who gave me permission, or my own child.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-gold">✦</span>
            <span>I won't upload photos of anyone else without their permission.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-gold">✦</span>
            <span>Portraits are of an ordinary person of the era. We don't depict holy figures.</span>
          </li>
        </ul>

        <div className="mt-5">
          <PrimaryButton
            busy={busy}
            busyLabel="Saving…"
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await api.acceptConsent();
                capture("consentAccepted");
                setState("ok");
              } catch {
                setErr("Couldn't save that. Please try again.");
              }
              setBusy(false);
            }}
          >
            I agree, continue
          </PrimaryButton>
          <ErrorText>{err}</ErrorText>
        </div>
      </div>
    </GildedFrame>
  );
}
